/**
 * Low-level backend request handler.
 * Auth is handled via HttpOnly cookies — no manual token injection needed.
 */
import type { HttpMethod } from '@/lib/enums';
import type { IApiRequestConfig, IRequestConfig } from '@/lib/types';
import { generateUniqueToken } from '@/lib/utils';
import { axiosInstance, HttpError } from './http';
import { AxiosHeaders } from 'axios';

type HeaderConfig = Pick<IApiRequestConfig, 'headers' | 'logRequest' | 'skipRefreshInterceptor'>;

/** The headers every backend call carries. Shared by axios and keepalive requests. */
function buildRequestHeaders(
  { headers, logRequest = true, skipRefreshInterceptor }: HeaderConfig,
  isFormData: boolean,
): AxiosHeaders {
  const customHeaders = new AxiosHeaders();
  if (!isFormData) {
    customHeaders.setContentType('application/json');
  }
  const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'UTC';
  customHeaders.set('X-User-Agent-Timezone', timezone);
  if (logRequest) {
    customHeaders.set('X-Request-Id', generateUniqueToken());
  }
  if (skipRefreshInterceptor) {
    customHeaders.set('X-Skip-Refresh', '1');
  }
  if (headers) {
    Object.keys(headers).forEach((key) => {
      customHeaders.set(key, headers[key]);
    });
  }
  return customHeaders;
}

/** The path below the API host: `{apiVersion}/{resource}[/{resourceId}]`. */
function buildResourcePath({
  resource,
  apiVersion,
  resourceId,
}: Pick<IApiRequestConfig, 'resource' | 'apiVersion' | 'resourceId'>): string {
  const path = apiVersion ? `${apiVersion}/${resource}` : `/${resource}`;
  return resourceId ? `${path}/${resourceId}` : path;
}

export async function backendRequest({
  url,
  resource,
  method,
  apiHost,
  apiVersion,
  params,
  data,
  resourceId,
  headers,
  responseType,
  logRequest = true,
  skipRefreshInterceptor,
  timeout,
  signal,
}: IApiRequestConfig) {
  const isFormData = data instanceof FormData;
  const customHeaders = buildRequestHeaders(
    { headers, logRequest, skipRefreshInterceptor },
    isFormData,
  );

  const hasFullUrl = !!url;
  if (!hasFullUrl) {
    url = buildResourcePath({ resource, apiVersion, resourceId });
  }

  const requestConfig: IRequestConfig = {
    method: method as HttpMethod,
    ...(responseType ? { responseType } : {}),
    ...(data ? { data } : {}),
    ...(params ? { params } : {}),
    ...(timeout ? { timeout } : {}),
    ...(signal ? { signal } : {}),
    headers: customHeaders,
    url: url as string,
  };

  if (!hasFullUrl) {
    requestConfig.baseURL = apiHost;
  }

  return axiosInstance(requestConfig);
}

/**
 * Browsers cap keepalive request bodies at 64 KiB (in total, across every keepalive
 * request still in flight). A bigger body makes `fetch` reject.
 */
export const KEEPALIVE_BODY_LIMIT_BYTES = 64 * 1024;

/** True when this JSON body is small enough to send with `keepalive`. */
export const fitsKeepalive = (body: string): boolean =>
  new TextEncoder().encode(body).length <= KEEPALIVE_BODY_LIMIT_BYTES;

/**
 * A JSON request sent with `fetch(…, { keepalive: true })`, so the browser still sends it
 * while the page closes. A normal axios call is cancelled when the tab goes away.
 *
 * Same URL, headers and cookies as {@link backendRequest}. It skips the axios interceptors,
 * so there is no 401 refresh — a page that is closing could not use one anyway. It settles
 * like the axios layer: `{ data }` on success, an {@link HttpError} on an HTTP error.
 *
 * Check the body with {@link fitsKeepalive} first; a body over the cap rejects.
 */
export async function keepaliveRequest({
  method,
  apiHost = '',
  apiVersion,
  resource,
  resourceId,
  data,
  headers,
  logRequest = true,
  skipRefreshInterceptor,
}: IApiRequestConfig): Promise<{ data: unknown }> {
  const path = buildResourcePath({ resource, apiVersion, resourceId });
  // The same URL axios builds from `baseURL` + `url` for backendRequest.
  const url = axiosInstance.getUri({ baseURL: apiHost || undefined, url: path });
  const requestHeaders = buildRequestHeaders(
    { headers, logRequest, skipRefreshInterceptor },
    false,
  );
  const response = await fetch(url, {
    method,
    keepalive: true,
    // axios is created with `withCredentials: true`; this is the fetch equivalent.
    credentials: 'include',
    headers: requestHeaders.toJSON() as Record<string, string>,
    body: data === undefined ? undefined : JSON.stringify(data),
  });
  const json = parseJsonBody(await response.text());
  if (!response.ok) {
    throw new HttpError(response.status, json);
  }
  return { data: json };
}

/** A JSON response body, or `undefined` when it is empty or not JSON (say, a proxy page). */
function parseJsonBody(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
